import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type { _PricingBlock as PricingBlockValue } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

/**
 * toppers OfferPage ``pricing_charts`` assembly: collapses the per-block
 * ``{% for block in page.pricing_charts %}`` loop in
 * ``cms/offer_page.html`` into a SINGLE ``{% include_react %}`` island. The
 * surrounding ``.offer-page__pricing`` wrapper + the ``{% if self.pricing_charts %}``
 * guard stay in ``offer_page.html``.
 *
 * NO WRAPPER (Fragment): the loop did NOT use ``{% include_block %}`` --
 * it MANUALLY unpacked each block's bound sub-fields server-side
 * (``{{ block.value.bound_blocks.headline }}`` etc.), so wagtail's ``w-block-*``
 * wrapper was never applied. The per-block DOM was bespoke; this map entry
 * reproduces it verbatim:
 *
 *     <h3>{{ headline }}</h3>
 *     <div class="offer-page__pricing-wrapper"> {table} </div>
 *     <div class="offer-page__pricing-mousetype"> {footnote} </div>
 *
 * SUB-FIELDS (``_PricingBlock``, all serialize to ``string | null``):
 * - ``headline`` (CharBlock): the ``{{ block.value.bound_blocks.headline }}``
 *   is NOT ``|safe`` (Django autoescape), so emit as an escaped React text node.
 *   When absent it rendered an empty ``<h3></h3>`` (the BoundBlock
 *   ``str()`` of an empty CharBlock is ``""``), so always render the ``<h3>``.
 * - ``table`` (nested wagtail ``TableBlock`` made client-renderable via
 *   ``ClientRenderingBarrier``): the value is the PRE-RENDERED ``<table>`` HTML
 *   string (verified the generated ``table`` type is ``string``, NOT a nullable
 *   ref). Raw-inject it inside a bare classless ``<div>``.
 *   ``.offer-page__pricing table`` is a descendant selector, so the table
 *   still matches under the wrapper.
 * - ``footnote`` (RichTextBlock): serialized rich-text HTML string (already
 *   includes its ``<div class="rich-text">`` wrapper, matching the
 *   ``{{ block.value.bound_blocks.footnote }}`` output). Raw-inject it DIRECTLY
 *   onto the ``.offer-page__pricing-mousetype`` div (the template emitted
 *   ``.offer-page__pricing-mousetype > .rich-text`` with no intermediate node),
 *   so no extra wrapper here.
 *
 * NO GLOBAL JS: static content (no carousel / no pre-hydration DOM injection).
 */
const pricingChartsComponentsMap = {
    pricing_block: (block: { value: Immutable<PricingBlockValue> }) => (
        <>
            <h3>{block.value.headline}</h3>
            <div className="offer-page__pricing-wrapper">
                {/* Bare classless <div>: no class/style so it stays
                    transparent to the parent's overflow, and
                    `.offer-page__pricing table` is a descendant selector, so
                    the table still matches under it. */}
                <div
                    dangerouslySetInnerHTML={{
                        __html: block.value.table ?? "",
                    }}
                />
            </div>
            <div
                className="offer-page__pricing-mousetype"
                dangerouslySetInnerHTML={{
                    __html: block.value.footnote ?? "",
                }}
            />
        </>
    ),
} as const;

export const OfferPagePricingCharts = (
    props: Immutable<{
        page: Pick<templates.OfferPageTemplate["page"], "pricing_charts">;
    }>,
): React.JSX.Element => {
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.pricing_charts.value}
            components={pricingChartsComponentsMap}
        />
    );
};
